var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import EditIcon from "@mui/icons-material/Edit";
import LoadingButton from "@mui/lab/LoadingButton";
import { EPaymentType, getPaymentString, } from "../../../types";
import { ModalNew } from "../../atoms/Modal/Modal";
const getButtonDescription = (player) => {
    const { payment } = player;
    if (!payment) {
        return "Выбрать";
    }
    if (payment.method === EPaymentType.PAID_BY_OTHER_PLAYER &&
        payment.parentPayment) {
        return `${payment.parentPayment.payerName} (${payment.parentPayment.amount}₽)`;
    }
    if (payment.amount === 0 && payment.method === EPaymentType.FREE) {
        return "Бесплатно";
    }
    return `${payment.amount}₽ (${getPaymentString(payment.method)})`;
};
export function Payment({ player, onPaymentSubmit, players, }) {
    var _a, _b, _c, _d, _e;
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Form state
    const [paymentMethod, setPaymentMethod] = useState(player.payment_method);
    const [amount, setAmount] = useState(((_b = (_a = player.payment) === null || _a === void 0 ? void 0 : _a.amount) === null || _b === void 0 ? void 0 : _b.toString()) || "");
    const [paymentId, setPaymentId] = useState(((_d = (_c = player.payment) === null || _c === void 0 ? void 0 : _c.parentPayment) === null || _d === void 0 ? void 0 : _d.id) || "");
    // Reset form when modal opens
    const handleOpen = () => {
        var _a, _b, _c, _d;
        setPaymentMethod(player.payment_method);
        setAmount(((_b = (_a = player.payment) === null || _a === void 0 ? void 0 : _a.amount) === null || _b === void 0 ? void 0 : _b.toString()) || "");
        setPaymentId(((_d = (_c = player.payment) === null || _c === void 0 ? void 0 : _c.parentPayment) === null || _d === void 0 ? void 0 : _d.id) || "");
        setIsOpen(true);
    };
    // Validation
    const isFormValid = useMemo(() => {
        if (!paymentMethod)
            return false;
        if (paymentMethod === EPaymentType.CASH) {
            const numAmount = Number(amount);
            return amount !== "" && !isNaN(numAmount) && numAmount > 0;
        }
        if (paymentMethod === EPaymentType.PAID_BY_OTHER_PLAYER) {
            return !!paymentId;
        }
        return true; // FREE
    }, [paymentMethod, amount, paymentId]);
    // Handle payment method change
    const handleMethodChange = (_, newMethod) => __awaiter(this, void 0, void 0, function* () {
        if (newMethod === null)
            return;
        // Instant submit for FREE - don't even update state, just submit and close
        if (newMethod === EPaymentType.FREE) {
            setIsSubmitting(true);
            try {
                yield onPaymentSubmit({ playerId: player.id, payment_method: newMethod, amount: 0, paymentId: "" }, {});
                setIsOpen(false);
            }
            catch (error) {
                console.error("Payment submit error:", error);
                setIsSubmitting(false);
            }
            return;
        }
        setPaymentMethod(newMethod);
    });
    // Submit handler
    const handleSubmit = (method = paymentMethod, submitAmount = Number(amount) || 0, submitPaymentId = paymentId) => __awaiter(this, void 0, void 0, function* () {
        setIsSubmitting(true);
        try {
            yield onPaymentSubmit({
                playerId: player.id,
                payment_method: method,
                amount: submitAmount,
                paymentId: submitPaymentId,
            }, {});
            setIsOpen(false);
        }
        catch (error) {
            console.error("Payment submit error:", error);
        }
        finally {
            setIsSubmitting(false);
        }
    });
    // If payment is confirmed, just show the description
    if ((_e = player.payment) === null || _e === void 0 ? void 0 : _e.isConfirmed) {
        return _jsx(_Fragment, { children: getButtonDescription(player) });
    }
    return (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ startIcon: _jsx(EditIcon, {}), variant: player.payment ? "contained" : "outlined", size: "small", onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleOpen();
                } }, { children: getButtonDescription(player) })), isOpen && (_jsx(ModalNew, Object.assign({ onClose: () => !isSubmitting && setIsOpen(false), close: true }, { children: _jsxs(Box, Object.assign({ sx: { p: 3, width: 360 } }, { children: [_jsxs(Typography, Object.assign({ variant: "h5", sx: { mb: 3 } }, { children: ["\u041E\u043F\u043B\u0430\u0442\u0430 \u043E\u0442 ", player.player.gameName || player.player.realName] })), _jsx(Typography, Object.assign({ variant: "subtitle2", color: "text.secondary", sx: { mb: 1 } }, { children: "\u0421\u043F\u043E\u0441\u043E\u0431 \u043E\u043F\u043B\u0430\u0442\u044B" })), _jsxs(ToggleButtonGroup, Object.assign({ value: paymentMethod, exclusive: true, onChange: handleMethodChange, fullWidth: true, sx: { mb: 3 }, disabled: isSubmitting }, { children: [_jsx(ToggleButton, Object.assign({ value: EPaymentType.CASH, sx: { fontSize: '1rem', py: 1.5 } }, { children: "\u041D\u0430\u043B\u0438\u0447\u043D\u044B\u0435" })), _jsx(ToggleButton, Object.assign({ value: EPaymentType.FREE, sx: { fontSize: '1rem', py: 1.5 } }, { children: "\u0411\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u043E" })), _jsx(ToggleButton, Object.assign({ value: EPaymentType.PAID_BY_OTHER_PLAYER, sx: { fontSize: '1rem', py: 1.5 } }, { children: "\u0414\u0440\u0443\u0433\u043E\u0439 \u0438\u0433\u0440\u043E\u043A" }))] })), paymentMethod === EPaymentType.CASH && (_jsx(TextField, { label: "\u0421\u0443\u043C\u043C\u0430", type: "number", fullWidth: true, value: amount, onChange: (e) => setAmount(e.target.value), InputProps: {
                                endAdornment: _jsx(InputAdornment, Object.assign({ position: "end" }, { children: "\u20BD" })),
                            }, disabled: isSubmitting, sx: { mb: 3 }, autoFocus: true })), paymentMethod === EPaymentType.PAID_BY_OTHER_PLAYER && (_jsxs(FormControl, Object.assign({ fullWidth: true, sx: { mb: 3 } }, { children: [_jsx(InputLabel, { children: "\u041A\u0442\u043E \u043E\u043F\u043B\u0430\u0442\u0438\u043B?" }), _jsx(Select, Object.assign({ value: paymentId, onChange: (e) => setPaymentId(e.target.value), label: "\u041A\u0442\u043E \u043E\u043F\u043B\u0430\u0442\u0438\u043B?", disabled: isSubmitting, MenuProps: {
                                        sx: {
                                            zIndex: 100001,
                                        },
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 300,
                                            }
                                        },
                                    } }, { children: players.length === 0 ? (_jsx(MenuItem, Object.assign({ value: "", disabled: true }, { children: "\u041D\u0435\u0442 \u0438\u0433\u0440\u043E\u043A\u043E\u0432 \u0441 \u043E\u043F\u043B\u0430\u0442\u043E\u0439" }))) : (players.map((p) => {
                                        var _a, _b;
                                        return (_jsxs(MenuItem, Object.assign({ value: ((_a = p.payment) === null || _a === void 0 ? void 0 : _a.id) || "" }, { children: [p.player.gameName || p.player.realName, " (", ((_b = p.payment) === null || _b === void 0 ? void 0 : _b.amount) || 0, "\u20BD)"] }), p.id));
                                    })) }))] }))), _jsx(LoadingButton, Object.assign({ variant: "contained", fullWidth: true, onClick: () => handleSubmit(), disabled: !isFormValid, loading: isSubmitting }, { children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" }))] })) })))] }));
}
